import { useEffect, useRef, useState } from "react";

const GRID_SIZE = 20;
const CELL_SIZE = 20;
const CANVAS_SIZE = GRID_SIZE * CELL_SIZE;
const DPR = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
const BASE_TICK_MS = 153;
const MIN_TICK_MS = 90;
const BASE_OBSTACLES = 6;
const MAX_OBSTACLES = 14;
const EAT_DURATION = 360;

const DIRS = {
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
};

const speedForApples = (n) =>
  Math.max(MIN_TICK_MS, BASE_TICK_MS - Math.floor(n / 5) * 13);

const obstacleCountForApples = (n) =>
  Math.min(MAX_OBSTACLES, BASE_OBSTACLES + Math.floor(n / 3));

const randomCell = (blocked) => {
  while (true) {
    const c = {
      x: Math.floor(Math.random() * GRID_SIZE),
      y: Math.floor(Math.random() * GRID_SIZE),
    };
    if (!blocked.some((b) => b.x === c.x && b.y === c.y)) return c;
  }
};

const generateObstacles = (snake, count, dir) => {
  const blocked = [...snake];
  for (let dx = -3; dx <= 3; dx++)
    for (let dy = -3; dy <= 3; dy++)
      blocked.push({ x: snake[0].x + dx, y: snake[0].y + dy });
  if (dir) {
    for (let i = 1; i <= 6; i++)
      blocked.push({ x: snake[0].x + dir.x * i, y: snake[0].y + dir.y * i });
  }
  const obs = [];
  for (let i = 0; i < count; i++) {
    const c = randomCell([...blocked, ...obs]);
    c.hue = 15 + Math.floor(Math.random() * 30);
    obs.push(c);
  }
  return obs;
};

const initialState = () => {
  const path = [
    { x: 10, y: 10 },
    { x: 9, y: 10 },
    { x: 8, y: 10 },
  ];
  const obstacles = generateObstacles(path, BASE_OBSTACLES, DIRS.ArrowRight);
  return {
    path,
    oldTail: { x: 7, y: 10 },
    food: randomCell([...path, ...obstacles]),
    obstacles,
    dir: DIRS.ArrowRight,
    nextDir: DIRS.ArrowRight,
    score: 0,
    applesEaten: 0,
    tickMs: BASE_TICK_MS,
    lost: false,
    paused: false,
    lastTick: performance.now(),
    eatTime: -Infinity,
  };
};

const drawBackground = (ctx) => {
  for (let y = 0; y < GRID_SIZE; y++)
    for (let x = 0; x < GRID_SIZE; x++) {
      ctx.fillStyle = (x + y) % 2 === 0 ? "#9ac259" : "#95bd54";
      ctx.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
    }
};

const drawApple = (ctx, food) => {
  const cx = food.x * CELL_SIZE + CELL_SIZE / 2;
  const cy = food.y * CELL_SIZE + CELL_SIZE / 2;
  const r = CELL_SIZE * 0.38;

  ctx.fillStyle = "#e53935";
  ctx.beginPath();
  ctx.arc(cx, cy + 1, r, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.beginPath();
  ctx.arc(cx - r * 0.35, cy - r * 0.25, r * 0.22, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = "#5d4037";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx, cy - r - 4);
  ctx.stroke();

  ctx.fillStyle = "#43a047";
  ctx.beginPath();
  ctx.ellipse(cx + 4, cy - r - 2, 4, 2, -Math.PI / 5, 0, Math.PI * 2);
  ctx.fill();
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const drawObstacles = (ctx, obstacles) => {
  obstacles.forEach((o) => {
    const x = o.x * CELL_SIZE;
    const y = o.y * CELL_SIZE;
    const pad = 2;
    const hue = o.hue || 25;
    ctx.fillStyle = `hsl(${hue}, 22%, 38%)`;
    roundRect(ctx, x + pad, y + pad, CELL_SIZE - pad * 2, CELL_SIZE - pad * 2, 4);
    ctx.fill();
    ctx.fillStyle = `hsl(${hue}, 22%, 46%)`;
    roundRect(ctx, x + pad, y + pad, CELL_SIZE - pad * 2, CELL_SIZE - pad * 2 - 2, 4);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.15)";
    ctx.fillRect(x + pad + 2, y + pad + 2, 4, 2);
  });
};

const cellCenter = (c) => ({
  x: c.x * CELL_SIZE + CELL_SIZE / 2,
  y: c.y * CELL_SIZE + CELL_SIZE / 2,
});

const lerp = (a, b, t) => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});

const drawSnake = (ctx, points, headDir, eatTime) => {
  if (points.length === 0) return;

  ctx.strokeStyle = "#2e7d32";
  ctx.lineWidth = CELL_SIZE * 0.72;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  points.forEach((p, i) => {
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.stroke();

  ctx.strokeStyle = "#66bb6a";
  ctx.lineWidth = CELL_SIZE * 0.38;
  ctx.stroke();

  const head = points[points.length - 1];
  const r = CELL_SIZE * 0.44;
  const facingAngle = Math.atan2(headDir.y, headDir.x);
  const eatProgress = Math.min(1, (performance.now() - eatTime) / EAT_DURATION);
  const mouthAngle = eatProgress < 1 ? Math.PI * 0.28 * Math.sin(eatProgress * Math.PI) : 0;
  const chomping = mouthAngle > 0.02;

  ctx.fillStyle = "#2e7d32";
  ctx.beginPath();
  if (chomping) {
    ctx.moveTo(head.x, head.y);
    ctx.arc(head.x, head.y, r, facingAngle + mouthAngle, facingAngle + Math.PI * 2 - mouthAngle);
    ctx.closePath();
  } else {
    ctx.arc(head.x, head.y, r, 0, Math.PI * 2);
  }
  ctx.fill();

  if (!chomping) {
    const off = CELL_SIZE * 0.2;
    const fwd = CELL_SIZE * 0.16;
    let e1, e2;
    if (headDir.x === 1)       { e1 = { x: head.x + fwd, y: head.y - off }; e2 = { x: head.x + fwd, y: head.y + off }; }
    else if (headDir.x === -1) { e1 = { x: head.x - fwd, y: head.y - off }; e2 = { x: head.x - fwd, y: head.y + off }; }
    else if (headDir.y === -1) { e1 = { x: head.x - off, y: head.y - fwd }; e2 = { x: head.x + off, y: head.y - fwd }; }
    else                       { e1 = { x: head.x - off, y: head.y + fwd }; e2 = { x: head.x + off, y: head.y + fwd }; }
    ctx.fillStyle = "white";
    ctx.beginPath(); ctx.arc(e1.x, e1.y, 3, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(e2.x, e2.y, 3, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#111";
    ctx.beginPath(); ctx.arc(e1.x, e1.y, 1.5, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(e2.x, e2.y, 1.5, 0, Math.PI * 2); ctx.fill();
  }
};

function Snake() {
  const canvasRef = useRef(null);
  const staticCanvasRef = useRef(null);
  const stateRef = useRef(initialState());
  const rafRef = useRef(null);
  const [, forceRender] = useState(0);

  const rebuildStatic = (obstacles) => {
    const sc = staticCanvasRef.current;
    if (!sc) return;
    const ctx = sc.getContext("2d");
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    drawBackground(ctx);
    drawObstacles(ctx, obstacles);
  };

  const buildPolyline = () => {
    const s = stateRef.current;
    const t = s.lost || s.paused
      ? 1
      : Math.min(1, (performance.now() - s.lastTick) / s.tickMs);
    const N = s.path.length;
    const points = [];
    points.push(lerp(cellCenter(s.oldTail), cellCenter(s.path[N - 1]), t));
    for (let i = N - 1; i >= 1; i--) points.push(cellCenter(s.path[i]));
    points.push(lerp(cellCenter(s.path[1] || s.path[0]), cellCenter(s.path[0]), t));
    return points;
  };

  const draw = () => {
    const canvas = canvasRef.current;
    const sc = staticCanvasRef.current;
    if (!canvas || !sc) return;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    const { food, dir, eatTime } = stateRef.current;
    ctx.drawImage(sc, 0, 0, CANVAS_SIZE, CANVAS_SIZE);
    drawApple(ctx, food);
    drawSnake(ctx, buildPolyline(), dir, eatTime);
  };

  const tick = () => {
    const s = stateRef.current;
    if (s.lost || s.paused) return;

    s.dir = s.nextDir;
    const head = { x: s.path[0].x + s.dir.x, y: s.path[0].y + s.dir.y };

    const hitWall = head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE;
    const hitSelf = s.path.some((seg) => seg.x === head.x && seg.y === head.y);
    const hitObstacle = s.obstacles.some((o) => o.x === head.x && o.y === head.y);
    if (hitWall || hitSelf || hitObstacle) {
      s.lost = true;
      forceRender((n) => n + 1);
      return;
    }

    const eating = head.x === s.food.x && head.y === s.food.y;
    s.path.unshift(head);
    if (eating) {
      s.applesEaten += 1;
      s.score += s.applesEaten;
      s.eatTime = performance.now();
      s.tickMs = speedForApples(s.applesEaten);
      s.oldTail = { ...s.path[s.path.length - 1] };
      const newCount = obstacleCountForApples(s.applesEaten);
      if (s.applesEaten % 3 === 0) {
        s.obstacles = generateObstacles(s.path, newCount, s.dir);
        rebuildStatic(s.obstacles);
      }
      s.food = randomCell([...s.path, ...s.obstacles]);
      forceRender((n) => n + 1);
    } else {
      s.oldTail = s.path.pop();
    }
    s.lastTick = performance.now();
  };

  const restart = () => {
    stateRef.current = initialState();
    rebuildStatic(stateRef.current.obstacles);
    forceRender((n) => n + 1);
  };

  useEffect(() => {
    const onKey = (e) => {
      const s = stateRef.current;
      if (e.key === "p" || e.key === "P") {
        s.paused = !s.paused;
        if (!s.paused) s.lastTick = performance.now();
        forceRender((n) => n + 1);
        return;
      }
      const d = DIRS[e.key];
      if (!d) return;
      e.preventDefault();
      if (d.x === -s.dir.x && d.y === -s.dir.y) return;
      s.nextDir = d;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    rebuildStatic(stateRef.current.obstacles);
    const loop = (now) => {
      const s = stateRef.current;
      if (!s.lost && !s.paused && now - s.lastTick >= s.tickMs) {
        tick();
      }
      draw();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const { score, applesEaten, lost, paused } = stateRef.current;

  return (
    <>
      <canvas ref={staticCanvasRef} width={CANVAS_SIZE * DPR} height={CANVAS_SIZE * DPR} style={{ display: "none" }} />
      <h1 className="display-5 fw-bold">Snake</h1>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1em" }}>
        <p>Score: {score} &nbsp;·&nbsp; Appels: {applesEaten} {paused && !lost && <strong> — gepauzeerd</strong>}</p>
        <canvas
          ref={canvasRef}
          width={CANVAS_SIZE * DPR}
          height={CANVAS_SIZE * DPR}
          style={{ width: CANVAS_SIZE, height: CANVAS_SIZE, border: "4px solid #578a34", borderRadius: "4px" }}
        />
        {lost && (
          <div style={{ textAlign: "center" }}>
            <p>Game Over — score: {score} ({applesEaten} appels)</p>
            <button className="btn btn-primary" onClick={restart}>Nieuw spel</button>
          </div>
        )}
        <p style={{ color: "#666", fontSize: "0.9em" }}>
          Pijltjestoetsen om te bewegen &nbsp;·&nbsp; P om te pauzeren &nbsp;·&nbsp;
          Raak de randen en stenen niet!
        </p>
        <p style={{ color: "#aaa", fontSize: "0.75em", marginTop: "0.5em" }}>
          Gemaakt door <strong>Siyar Yildirim</strong> tijdens zijn stage 🐍
        </p>
      </div>
    </>
  );
}

export default Snake;
